import React, { useState, useEffect } from 'react';
import { Send, CheckCircle, User, Mail, Phone, Building2, Globe, FileText, MessageSquare } from 'lucide-react';
import './ProjectRequestForm.css';

const ProjectRequestForm = ({ packageName, tier }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    businessName: '',
    businessType: '',
    website: '',
    hasLogo: '',
    description: '',
    timeline: '',
    additionalNotes: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const addOnsSelected = tier.addOns && tier.addOns.length > 0
    ? tier.addOns.map(a => a.name).join(', ')
    : 'None';

  const addOnsTotal = tier.addOns ? tier.addOns.reduce((sum, a) => sum + a.price, 0) : 0;

  // Scroll to success message when submitted
  useEffect(() => {
    if (submitted) {
      setTimeout(() => {
        document.getElementById('project-request')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [submitted]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Send via Web3Forms with structured data
    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_key: 'f6ab0418-f6aa-47f5-b1da-89cb2a4503f4',
          subject: `🟢 New Project Request: ${packageName} - ${tier.name} | ${formData.businessName}`,
          from_name: formData.name,
          replyto: formData.email,
          // Structured fields that Web3Forms will format nicely
          'Package': packageName,
          'Tier': `${tier.name} (${tier.price})`,
          'Monthly Fee': tier.monthly,
          'Add-ons': addOnsSelected + (addOnsTotal > 0 ? ` (+$${addOnsTotal})` : ''),
          '---': '---',
          'Client Name': formData.name,
          'Client Email': formData.email,
          'Client Phone': formData.phone || 'Not provided',
          'Business Name': formData.businessName,
          'Business Type': formData.businessType,
          'Current Website': formData.website || 'None',
          'Has Logo/Branding': formData.hasLogo,
          '----': '---',
          'Project Description': formData.description,
          'Preferred Timeline': formData.timeline || 'No preference',
          'Additional Notes': formData.additionalNotes || 'None',
        }),
      });

      const data = await response.json();
      if (data.success) {
        setSubmitted(true);
      } else {
        console.error('Web3Forms error:', data);
        alert('Something went wrong. Please try again.');
      }
    } catch (err) {
      console.error('Submission error:', err);
      alert('Connection error. Please try again.');
    }

    setLoading(false);
  };

  if (submitted) {
    return (
      <section className="project-request section" id="project-request">
        <div className="container">
          <div className="project-request__success">
            <div className="project-request__success-icon">
              <CheckCircle size={52} />
            </div>
            <h3>Request submitted!</h3>
            <p>
              Our development team has received your project request. We'll review your
              details and get back to you within 24 hours with a project plan, timeline,
              and next steps.
            </p>
            <div className="project-request__success-summary">
              <div className="project-request__success-row">
                <span>Package</span>
                <strong>{packageName}</strong>
              </div>
              <div className="project-request__success-row">
                <span>Tier</span>
                <strong>{tier.name}</strong>
              </div>
              {tier.addOns && tier.addOns.length > 0 && (
                <div className="project-request__success-row">
                  <span>Add-ons</span>
                  <strong>{tier.addOns.length} selected</strong>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="project-request section" id="project-request">
      <div className="container">
        <div className="project-request__wrapper">
          {/* Left: Summary */}
          <div className="project-request__sidebar">
            <h3 className="project-request__sidebar-title">Your Selection</h3>
            <div className="project-request__sidebar-card">
              <div className="project-request__sidebar-row">
                <span>Package</span>
                <strong>{packageName}</strong>
              </div>
              <div className="project-request__sidebar-row">
                <span>Tier</span>
                <strong>{tier.name}</strong>
              </div>
              <div className="project-request__sidebar-row">
                <span>One-time</span>
                <strong>{tier.price}</strong>
              </div>
              <div className="project-request__sidebar-row">
                <span>Monthly</span>
                <strong>{tier.monthly}</strong>
              </div>
              {tier.addOns && tier.addOns.length > 0 && (
                <>
                  <div className="project-request__sidebar-divider" />
                  <p className="project-request__sidebar-label">Add-ons:</p>
                  {tier.addOns.map((addon) => (
                    <div key={addon.id} className="project-request__sidebar-addon">
                      <CheckCircle size={14} />
                      <span>{addon.name}</span>
                    </div>
                  ))}
                </>
              )}
            </div>
            <p className="project-request__sidebar-note">
              50% deposit required to start. Remaining 50% due on delivery.
            </p>
          </div>

          {/* Right: Form */}
          <div className="project-request__form-area">
            <div className="project-request__form-header">
              <h2 className="section-title">Tell us about your business</h2>
              <p className="project-request__form-desc">
                Fill in the details below so our development team can get started on your project. We'll reach out within 24 hours.
              </p>
            </div>

            <form className="project-request__form" onSubmit={handleSubmit}>
              <div className="project-request__field">
                <label><User size={14} /> Full Name *</label>
                <input type="text" name="name" placeholder="John Smith" value={formData.name} onChange={handleChange} required />
              </div>

              <div className="project-request__row">
                <div className="project-request__field">
                  <label><Mail size={14} /> Email *</label>
                  <input type="email" name="email" placeholder="john@business.com" value={formData.email} onChange={handleChange} required />
                </div>
                <div className="project-request__field">
                  <label><Phone size={14} /> Phone</label>
                  <input type="tel" name="phone" placeholder="+1 (555) 000-0000" value={formData.phone} onChange={handleChange} />
                </div>
              </div>

              <div className="project-request__row">
                <div className="project-request__field">
                  <label><Building2 size={14} /> Business Name *</label>
                  <input type="text" name="businessName" placeholder="Your Business Name" value={formData.businessName} onChange={handleChange} required />
                </div>
                <div className="project-request__field">
                  <label><FileText size={14} /> Business Type *</label>
                  <select name="businessType" value={formData.businessType} onChange={handleChange} required>
                    <option value="">Select type...</option>
                    <option value="Restaurant / Cafe / Food">Restaurant / Cafe / Food</option>
                    <option value="Salon / Barbershop / Spa">Salon / Barbershop / Spa</option>
                    <option value="Trade (Plumber, Electrician, etc.)">Trade (Plumber, Electrician, etc.)</option>
                    <option value="Personal Trainer / Gym">Personal Trainer / Gym</option>
                    <option value="Consultant / Coach">Consultant / Coach</option>
                    <option value="Retail / Shop">Retail / Shop</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              <div className="project-request__field">
                <label><Globe size={14} /> Current Website (if any)</label>
                <input type="url" name="website" placeholder="https://yourbusiness.com" value={formData.website} onChange={handleChange} />
              </div>

              <div className="project-request__field">
                <label>Do you have a logo or branding? *</label>
                <select name="hasLogo" value={formData.hasLogo} onChange={handleChange} required>
                  <option value="">Select...</option>
                  <option value="Yes — logo and brand colors">Yes — I have a logo and brand colors</option>
                  <option value="Yes — logo only, no brand kit">Yes — logo only, no brand kit</option>
                  <option value="No — I need one created">No — I need one created</option>
                </select>
              </div>

              <div className="project-request__field">
                <label><MessageSquare size={14} /> Tell us about your business and what you need *</label>
                <textarea name="description" placeholder="What does your business do? What's the goal of this website? Any specific features or pages you want?" rows="4" value={formData.description} onChange={handleChange} required />
              </div>

              <div className="project-request__field">
                <label>Preferred timeline</label>
                <select name="timeline" value={formData.timeline} onChange={handleChange}>
                  <option value="No preference">No preference</option>
                  <option value="ASAP — as fast as possible">ASAP — as fast as possible</option>
                  <option value="Within 1–2 weeks">Within 1–2 weeks</option>
                  <option value="Within 2–4 weeks">Within 2–4 weeks</option>
                  <option value="I'm flexible">I'm flexible</option>
                </select>
              </div>

              <div className="project-request__field">
                <label>Anything else we should know?</label>
                <textarea name="additionalNotes" placeholder="Special requirements, inspirations, competitors you like, etc." rows="3" value={formData.additionalNotes} onChange={handleChange} />
              </div>

              <button type="submit" className="btn-primary project-request__submit" disabled={loading}>
                <Send size={18} />
                {loading ? 'Submitting...' : 'Submit Project Request'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProjectRequestForm;
